import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../../../services/auth/auth.service';

@Component({
  selector: 'app-forget-password',
  templateUrl: './forget-password.component.html',
  styleUrl: './forget-password.component.scss',
  standalone: false,
})
export class ForgetPasswordComponent implements OnInit, OnDestroy, OnChanges {
  @Input() showDialog: boolean = false;
  @Input() userInformation: { email: string; password: string } = { email: '', password: '' };
  @Output() passwordChanged = new EventEmitter<string>(); // Emitir cuando la contraseña haya cambiado
  @Output() onCancel = new EventEmitter<boolean>();
  loading: boolean = false;
  recoverAccountForm: FormGroup<any>;
  requestCodeForm: FormGroup<any>;
  passwordFieldType: string = 'password';
  messages: any[] = [{ severity: 'warn', detail: 'Las contraseñas deben tener como mínimo 1 número, 1 mayúscula y 1 minúscula' }];
  validationMessages = {
    email: [
      { type: 'required', msg: 'Este campo es requerido.' },
      { type: 'minlength', msg: 'Debe tener mínimo de 6 caracteres de largo.' },
      { type: 'pattern', msg: 'El email no es válido.' },
    ],
    code: [{ type: 'required', msg: 'El código de confirmación es obligatorio.' }],
    newPassword: [
      { type: 'required', msg: 'Este campo es requerido.' },
      { type: 'minlength', msg: 'Debe tener mínimo de 12 caracteres de largo.' },
      {
        type: 'pattern',
        msg: 'La contraseña debe contar con al menos 1 número, 1 letra mayúscula, y 1 minúscula.',
      },
    ],
    confirmNewPassword: [
      { type: 'required', msg: 'Este campo es requerido.' },
      { type: 'passwordMismatch', msg: 'Las contraseñas no coinciden.' },
    ],
  };
  stepCodeToRecoverPassword: number = 1;

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder
  ) {
    this.recoverAccountForm = this.formBuilder.group({
      email: ['', Validators.compose([Validators.required, Validators.minLength(5), Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$')])],
    });

    this.requestCodeForm = this.formBuilder.group(
      {
        code: ['', Validators.required],
        newPassword: [
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(12),
            Validators.pattern(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).+$/), // Expresión regular para la validación
          ]),
        ],
        confirmNewPassword: ['', Validators.required],
      },
      { validators: this.passwordMatchValidator } // Validador personalizado
    );
  }

  ngOnInit(): void {
    // Prefill email if provided from parent (login form)
    const email = this.userInformation?.email || '';
    if (email) {
      this.recoverAccountForm.get('email')?.setValue(email);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userInformation']) {
      const email = this.userInformation?.email || '';
      if (email) {
        this.recoverAccountForm.get('email')?.setValue(email);
      }
    }
    if (changes['showDialog'] && this.showDialog) {
      // When dialog opens, ensure step 1 and prefilled email remain visible
      this.stepCodeToRecoverPassword = 1;
      const email = this.userInformation?.email || '';
      if (email) {
        this.recoverAccountForm.get('email')?.setValue(email);
      }
    }
  }

  ngOnDestroy(): void {
    this.showDialog = false;
    this.onCancel.emit(false);
  }

  // Alternar la visibilidad de la contraseña
  togglePasswordVisibility() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  // Validador para asegurar que las contraseñas coincidan
  private passwordMatchValidator(group: FormGroup) {
    const newPassword = group.get('newPassword')?.value;
    const confirmNewPassword = group.get('confirmNewPassword')?.value;
    return newPassword === confirmNewPassword ? null : { passwordMismatch: true };
  }

  // Solicitar el código de confirmación para restablecer la contraseña
  async requestCodeToConfirmAccount(): Promise<void> {
    try {
      this.loading = true;
      const email = this.recoverAccountForm.get('email')?.value;
      await this.authService.forgotPassword(email);
      this.stepCodeToRecoverPassword = 2;
      this.loading = false;
    } catch (error) {
      console.error('Error al solicitar código:', error);
      this.loading = false;
    }
  }

  // Validar el código de confirmación y establecer la nueva contraseña
  async validateCodeAndConfirmReset(): Promise<void> {
    try {
      this.loading = true;
      const { code, newPassword } = this.requestCodeForm.value;
      const email = this.recoverAccountForm.get('email')?.value;

      await this.authService.confirmForgotPassword(email, code, newPassword);
      this.passwordChanged.emit(newPassword);
      this.showDialog = false;
      this.loading = false;
    } catch (error) {
      console.error('Error al confirmar el restablecimiento de contraseña:', error);
      this.loading = false;
    }
  }

  resetComponentState(): void {
    this.showDialog = false;
    this.stepCodeToRecoverPassword = 1; // Vuelve al paso inicial
    this.recoverAccountForm.reset();
    this.requestCodeForm.reset();
    this.loading = false;
    this.passwordFieldType = 'password'; // Reinicia el estado de visibilidad de la contraseña
    this.onCancel.emit(false); // Emite el evento para notificar al componente padre
  }
}
